import { useMemo, useState, useCallback } from 'react'
import { ChevronLeft, ChevronRight, SlidersHorizontal } from 'lucide-react'
import { ChunkCard } from './ChunkCard'
import type { ContextChunkSummary } from '@/types/contextEngine'

// ─── Filter / sort types ────────────────────────────────────────────────────

type SortField = 'confidence' | 'date' | 'version'
type SortDir = 'asc' | 'desc'

const SOURCE_OPTIONS: { value: ContextChunkSummary['sourceType'] | 'all'; label: string }[] = [
  { value: 'all', label: 'All Sources' },
  { value: 'document', label: 'Document' },
  { value: 'audio', label: 'Audio' },
  { value: 'interview', label: 'Interview' },
  { value: 'artifact_feedback', label: 'Feedback' },
  { value: 'manual_edit', label: 'Manual' },
  { value: 'inference', label: 'Inferred' },
]

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'date', label: 'Date' },
  { value: 'confidence', label: 'Confidence' },
  { value: 'version', label: 'Version' },
]

const PAGE_SIZE = 20

// ─── Props ──────────────────────────────────────────────────────────────────

interface ChunkListProps {
  chunks: ContextChunkSummary[]
  onUpdate: (chunkId: string, updates: { text?: string }) => Promise<void>
  onArchive: (chunkId: string) => Promise<void>
}

export function ChunkList({ chunks, onUpdate, onArchive }: ChunkListProps) {
  // Filter state
  const [sourceFilter, setSourceFilter] = useState<ContextChunkSummary['sourceType'] | 'all'>('all')
  const [subDomainFilter, setSubDomainFilter] = useState<string>('all')
  const [minConfidence, setMinConfidence] = useState(0)
  const [showFilters, setShowFilters] = useState(false)

  // Sort state
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  // Pagination
  const [page, setPage] = useState(0)

  // Unique sub-domains for filter dropdown
  const subDomains = useMemo(() => {
    const subs = new Set<string>()
    for (const c of chunks) {
      if (c.subDomain) subs.add(c.subDomain)
    }
    return Array.from(subs).sort()
  }, [chunks])

  // Apply filters + sort
  const filtered = useMemo(() => {
    let result = chunks.filter((c) => c.status === 'active')

    if (sourceFilter !== 'all') {
      result = result.filter((c) => c.sourceType === sourceFilter)
    }
    if (subDomainFilter !== 'all') {
      result = result.filter((c) => c.subDomain === subDomainFilter)
    }
    if (minConfidence > 0) {
      result = result.filter((c) => c.confidence >= minConfidence / 100)
    }

    result.sort((a, b) => {
      let cmp = 0
      switch (sortField) {
        case 'confidence':
          cmp = a.confidence - b.confidence
          break
        case 'date':
          cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
          break
        case 'version':
          cmp = a.version - b.version
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })

    return result
  }, [chunks, sourceFilter, subDomainFilter, minConfidence, sortField, sortDir])

  // Paginate
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safeePage = Math.min(page, totalPages - 1)
  const paged = filtered.slice(safeePage * PAGE_SIZE, (safeePage + 1) * PAGE_SIZE)

  // Reset page when filters change
  const handleFilterChange = useCallback(
    <T,>(setter: React.Dispatch<React.SetStateAction<T>>) =>
      (value: T) => {
        setter(value)
        setPage(0)
      },
    [],
  )

  if (chunks.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-bg-secondary p-8 text-center">
        <p className="text-sm text-text-secondary">No chunks in this domain yet.</p>
        <p className="mt-1 text-xs text-text-tertiary">
          Upload a document or start an interview to populate this domain.
        </p>
      </div>
    )
  }

  return (
    <div>
      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs transition-colors ${
            showFilters
              ? 'border-accent-amber/30 bg-accent-amber/10 text-accent-amber'
              : 'border-border bg-bg-secondary text-text-secondary hover:border-border-hover'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filters
          {(sourceFilter !== 'all' || subDomainFilter !== 'all' || minConfidence > 0) && (
            <span className="ml-1 rounded-full bg-accent-amber px-1.5 text-[10px] font-bold text-bg-deepest">
              {[sourceFilter !== 'all', subDomainFilter !== 'all', minConfidence > 0].filter(
                Boolean,
              ).length}
            </span>
          )}
        </button>

        {/* Sort */}
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-text-tertiary">Sort:</span>
          <select
            value={sortField}
            onChange={(e) => handleFilterChange(setSortField)(e.target.value as SortField)}
            className="rounded-md border border-border bg-bg-secondary px-2 py-1 text-xs text-text-primary outline-none"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="rounded-md border border-border bg-bg-secondary px-2 py-1 text-xs text-text-secondary hover:border-border-hover"
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        {/* Count */}
        <span className="text-xs text-text-tertiary">
          {filtered.length} chunk{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-border bg-bg-secondary p-4">
          {/* Source type */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-text-tertiary">Source</span>
            <select
              value={sourceFilter}
              onChange={(e) =>
                handleFilterChange(setSourceFilter)(
                  e.target.value as ContextChunkSummary['sourceType'] | 'all',
                )
              }
              className="rounded-md border border-border bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none"
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {/* Sub-domain */}
          {subDomains.length > 0 && (
            <label className="flex flex-col gap-1">
              <span className="text-xs text-text-tertiary">Sub-domain</span>
              <select
                value={subDomainFilter}
                onChange={(e) => handleFilterChange(setSubDomainFilter)(e.target.value)}
                className="rounded-md border border-border bg-bg-tertiary px-2 py-1.5 text-xs text-text-primary outline-none"
              >
                <option value="all">All</option>
                {subDomains.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}

          {/* Confidence threshold */}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-text-tertiary">
              Min confidence: {minConfidence}%
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={10}
              value={minConfidence}
              onChange={(e) => handleFilterChange(setMinConfidence)(Number(e.target.value))}
              className="w-32 accent-accent-amber"
            />
          </label>

          {/* Reset */}
          {(sourceFilter !== 'all' || subDomainFilter !== 'all' || minConfidence > 0) && (
            <button
              onClick={() => {
                setSourceFilter('all')
                setSubDomainFilter('all')
                setMinConfidence(0)
                setPage(0)
              }}
              className="text-xs text-accent-amber hover:underline"
            >
              Reset filters
            </button>
          )}
        </div>
      )}

      {/* Chunk cards */}
      {paged.length > 0 ? (
        <div className="space-y-3">
          {paged.map((chunk) => (
            <ChunkCard
              key={chunk.chunkId}
              chunk={chunk}
              onUpdate={onUpdate}
              onArchive={onArchive}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-bg-secondary p-6 text-center">
          <p className="text-sm text-text-secondary">No chunks match the current filters.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safeePage === 0}
            className="rounded-md border border-border bg-bg-secondary p-1.5 text-text-secondary transition-colors hover:border-border-hover disabled:opacity-30"
          >
            <ChevronLeft size={16} />
          </button>
          <span className="font-mono text-xs text-text-tertiary">
            {safeePage + 1} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safeePage >= totalPages - 1}
            className="rounded-md border border-border bg-bg-secondary p-1.5 text-text-secondary transition-colors hover:border-border-hover disabled:opacity-30"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  )
}
